import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "src/@/components/ui/accordion";
import { toast } from 'src/@/components/ui/use-toast';
import v2_actionSendAnalysisFeedback from 'src/actions/v2/ai/v2_actionSendAnalysisFeedback';
import { Divider } from '@mui/material';
import { soNumeros } from 'src/utils/functions';

export default function ChatAIAnalysisMessage({ data, user }) {
    const { t } = useTranslation('chat');

    const mappSentiment = {
        "1": { label: t('aiAnalysis.sentiments.angry'), bg: "bg-[#FBFBFB] border-red-300 text-red-700", icon: "emotion-angry" },
        "2": { label: t('aiAnalysis.sentiments.frustrated'), bg: "bg-orange-50 border-orange-200 text-orange-500", icon: "emotion-sad" },
        "3": { label: t('aiAnalysis.sentiments.neutral'), bg: "bg-white border-gray-200 text-gray-700", icon: "emotion-normal" },
        "4": { label: t('aiAnalysis.sentiments.satisfied'), bg: "bg-green-50 border-green-200 text-green-700", icon: "emotion-happy" },
        "5": { label: t('aiAnalysis.sentiments.verySatisfied'), bg: "bg-blue-50 border-blue-200 text-blue-700", icon: "emotion-satisfied" },
        "negative": { label: t('aiAnalysis.sentiments.negative'), bg: "bg-red-100 border-red-200 text-red-700", icon: "emotion-unhappy" },
        "neutral": { label: t('aiAnalysis.sentiments.neutral'), bg: "bg-gray-100 border-gray-200 text-gray-700", icon: "emotion-normal" },
        "positive": { label: t('aiAnalysis.sentiments.positive'), bg: "bg-green-100 border-green-200 text-green-700", icon: "emotion-happy" }
    };

    const aiAnalysis = data?.result;
    const [feedbackSent, setFeedbackSent] = useState(data?.feedback ? true : false);
    const [loadingFeedback, setLoadingFeedback] = useState(false);

    const sentiment = soNumeros(aiAnalysis?.sentiment)

    // Formatar a data
    const formattedDate = data?.date 
        ? new Date(data.date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '';

    const handleFeedback = async (vote) => {
        if (feedbackSent) return;
        
        try {
            setLoadingFeedback(true);
            await v2_actionSendAnalysisFeedback(data._id, {
                vote: vote,
                user_id: user.id
            });
            setFeedbackSent(true);
            toast({
                description: t('aiAnalysis.feedbackThanks'),
                duration: 3000,
            });
        } catch (error) {
            console.error('Erro ao enviar feedback:', error);
            toast({
                variant: 'destructive',
                description: t('aiAnalysis.feedbackError'),
                duration: 3000,
            });
        } finally {
            setLoadingFeedback(false);
        }
    };

    return (
        <div className={`w-full rounded-lg p-3 shadow-sm border ${
            mappSentiment[sentiment]?.bg || 'bg-white border-gray-200'
        }`}>
            {/* Cabeçalho com Título, Data e Sentimento */}
            <div className="flex flex-col gap-2 mb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Iconify icon="carbon:ai-status" className="w-5 h-5 text-violet-600" />
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-violet-600">{t('aiAnalysis.title')}</span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-500">{formattedDate}</span>
                        </div>
                    </div>
                    <Badge 
                        className={`px-2 text-xs flex items-center gap-1 border ${mappSentiment[sentiment]?.bg}`}
                    >
                        <Iconify 
                            icon={`carbon:${mappSentiment[sentiment]?.icon}`} 
                            className="w-3.5 h-3.5" 
                        />
                        {mappSentiment[sentiment]?.label}
                    </Badge>
                </div>
            </div>

           <Divider className="border-dashed border-gray-200 mt-4" />

            <div className="text-xs text-gray-600 mb-2 mt-2">
                {aiAnalysis?.resume}
            </div>

            {/* Seção de Feedback - Só mostra se não houver feedback */}
            {!data?.feedback && (
                <div className="flex items-center justify-between border-t border-gray-100 mt-3 pt-3">
                    <span className="text-xs text-gray-500">{t('aiAnalysis.feedbackQuestion')}</span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleFeedback("positive")}
                            disabled={feedbackSent || loadingFeedback}
                            className={`p-1.5 rounded-full transition-colors ${
                                feedbackSent ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-green-50 text-gray-600 hover:text-green-600'
                            }`}
                        >
                            <Iconify icon="carbon:thumbs-up" className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handleFeedback("negative")}
                            disabled={feedbackSent || loadingFeedback}
                            className={`p-1.5 rounded-full transition-colors ${
                                feedbackSent ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                            }`}
                        >
                            <Iconify icon="carbon:thumbs-down" className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Accordion com Detalhes */}
            <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="details" className="border-none mt-4">
                    <AccordionTrigger className="text-xs font-medium text-violet-600">
                        {t('aiAnalysis.viewDetails')}
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-8 pt-2">
                            {/* Resumo Completo */}
                   

                            {/* Alertas */}
                            <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                    <Iconify icon="carbon:warning" className="w-4 h-4 text-violet-600" />
                                    <span className="text-xs font-bold text-slate-600">{t('aiAnalysis.alerts')}</span>
                                </div>
                                <div className="space-y-1.5 pl-6">
                                    {aiAnalysis?.alerts?.length > 0 ? (
                                        aiAnalysis.alerts.map((alerta, index) => (
                                            <div key={index} className="flex items-center gap-1.5 p-3 bg-white border border-red-100 text-slate-700 rounded-md text-xs">
                                                <Iconify icon="carbon:warning-alt" className="w-3.5 h-3.5 flex-shrink-0 text-red-500" />
                                                {alerta}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-xs text-gray-500">{t('aiAnalysis.noAlerts')}</div>
                                    )}
                                </div>
                            </div>

                            {/* Assuntos */}
                            <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                    <Iconify icon="carbon:list-checked" className="w-4 h-4 text-violet-600" />
                                    <span className="text-xs font-bold text-slate-600">{t('aiAnalysis.subjects')}</span>
                                </div>
                                <div className="flex flex-wrap gap-1 pl-6">
                                    {aiAnalysis?.subject?.length > 0 ? (
                                        aiAnalysis.subject.map((assunto, index) => (
                                            <Badge key={index} variant="outline" className="bg-white border-violet-200 text-xs px-3 py-1">
                                                {assunto}
                                            </Badge>
                                        ))
                                    ) : (
                                        <div className="text-xs text-gray-500">{t('aiAnalysis.noSubjects')}</div>
                                    )}
                                </div>
                            </div>

                            {/* Sugestões */}
                            <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                    <Iconify icon="carbon:idea" className="w-4 h-4 text-violet-600" />
                                    <span className="text-xs font-bold text-slate-600">{t('aiAnalysis.suggestions')}</span>
                                </div>
                                <ul className="space-y-1.5 pl-6">
                                    {aiAnalysis?.suggestions?.length > 0 ? (
                                        aiAnalysis.suggestions.map((sugestao, index) => (
                                            <li key={index} className="flex items-start gap-1.5 text-xs text-gray-600">
                                                <Iconify icon="carbon:arrow-right" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                                <span>{sugestao}</span>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="text-xs text-gray-500">{t('aiAnalysis.noSuggestions')}</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}

ChatAIAnalysisMessage.propTypes = {
    data: PropTypes.object.isRequired,
}; 